import { useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, LayoutDashboard } from 'lucide-react'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { DashboardBoard } from '@/components/dashboard/DashboardBoard'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useDashboard } from '@/hooks/useDashboard'
import { usePermission } from '@/hooks/usePermission'
import { useWorkspace } from '@/hooks/useWorkspace'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { Dashboard, DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { logger } from '@/utils/logger'

export function DashboardPage() {
  const { dashboardId: routeDashboardId } = useParams<{ dashboardId?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspace()

  const {
    dashboard, dashboards, createDashboard, updateDashboard, deleteDashboard, queryAnalytics,
    createWidget, deleteWidget, resizeWidget, reorderWidgets,
    isSaving, isLoading,
  } = useDashboard(routeDashboardId)

  const [widgets, setWidgets] = useState<DashboardWidget[]>([])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [widgetData, setWidgetData] = useState<Record<string, DashboardAnalyticsResponse | undefined>>({})
  const widgetCursors = useRef<Record<string, {lastCreatedAt: string; lastId: string} | null>>({})

  useEffect(() => {
    if (dashboard?.id && !routeDashboardId) {
      navigate(`/dashboard/${dashboard.id}`, { replace: true })
    }
  }, [dashboard?.id, routeDashboardId, navigate])

  useEffect(() => {
    if (dashboard?.widgets) {
      setWidgets(dashboard.widgets)
    } else {
      setWidgets([])
    }
  }, [dashboard])

  useEffect(() => {
    let cancelled = false
    async function loadWidgetData() {
      const entries = await Promise.all(
        widgets.map(async (widget) => {
          try {
            const data = await queryAnalytics({
              dataSource: widget.dataSource,
              config: widget.dataSourceConfig ?? {},
              dashboardId: dashboard?.id ?? undefined,
            })
            return [widget.id, data] as const
          } catch {
            logger.warn('Failed to load widget data', widget.dataSource)
            return [widget.id, { message: 'Failed to load widget data' } as DashboardAnalyticsResponse] as const
          }
        })
      )
      if (!cancelled) {
        setWidgetData(Object.fromEntries(entries))
        for (const [id, data] of entries) {
          if (data?.lastCreatedAt && data?.lastId) {
            widgetCursors.current[id] = { lastCreatedAt: data.lastCreatedAt, lastId: data.lastId }
          }
        }
      }
    }
    // Only widgets without data are loaded, so new widgetData never triggers a refetch.
    if (widgets.length > 0 && widgets.some(w => !widgetData[w.id])) {
      void loadWidgetData()
    }
    return () => {
      cancelled = true
    }
  }, [widgets, queryAnalytics, widgetData, dashboard?.id])

  const persistSettings = (overrides: Partial<{
    title: string
    description: string | null
    isDefault: boolean
  }>) => {
    if (!dashboard) return
    const payload = {
      title: overrides?.title ?? dashboard.title,
      description: overrides?.description ?? dashboard.description,
      isDefault: overrides?.isDefault ?? dashboard.isDefault,
    }
    if (dashboard.id) {
      updateDashboard({ dashboardId: dashboard.id, data: payload })
    } else {
      createDashboard({ ...payload, isDefault: true })
    }
  }

  const handleCreateWidget = useCallback(async (widgetDataToCreate: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'dashboardId'> & { tempId?: string }) => {
    if (!dashboard?.id) return
    const { tempId, ...rest } = widgetDataToCreate
    try {
      const created = await createWidget({
        dashboardId: dashboard.id,
        ...rest
      })
      setWidgets((prev) => {
        if (tempId && prev.some(w => w.id === tempId)) {
          return prev.map(w => w.id === tempId ? { ...created } : w)
        }
        return [...prev, created]
      })
    } catch (err) {
      logger.error('Failed to create widget', err, 'expected')
      if (tempId) {
        setWidgets((prev) => prev.filter((w) => w.id !== tempId))
      }
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'dashboard'] })
      toast.error('Failed to create widget')
    }
  }, [dashboard?.id, createWidget, queryClient, currentWorkspaceId])

  const handleDeleteWidget = useCallback(async (widgetId: string) => {
    if (!dashboard?.id) return
    if (widgetId.startsWith('temp-')) {
      setWidgets((prev) => prev.filter((w) => w.id !== widgetId))
      return
    }
    try {
      await deleteWidget({ dashboardId: dashboard.id, widgetId })
    } catch (err) {
      logger.error('Failed to delete widget', err, 'expected')
    }
  }, [dashboard?.id, deleteWidget])

  const handleResizeWidget = useCallback(async (widgetId: string, w: number, h: number) => {
    if (!dashboard?.id || widgetId.startsWith('temp-')) return
    try {
      await resizeWidget({ dashboardId: dashboard.id, widgetId, w, h })
    } catch (err) {
      logger.error('Failed to resize widget', err, 'expected')
    }
  }, [dashboard?.id, resizeWidget])

  const handleReorderWidgets = useCallback(async (widgetIds: string[]) => {
    if (!dashboard?.id) return
    const validIds = widgetIds.filter(id => !id.startsWith('temp-'))
    try {
      await reorderWidgets({ dashboardId: dashboard.id, data: { widgetIds: validIds } })
    } catch (err) {
      logger.error('Failed to reorder widgets', err, 'expected')
    }
  }, [dashboard?.id, reorderWidgets])

  const handleLoadMore = useCallback(async (
    widgetId: string,
    dataSource: string,
    config: Record<string, unknown>,
  ) => {
    const cursor = widgetCursors.current[widgetId]
    const newData = await queryAnalytics({
      dataSource,
      dashboardId: dashboard?.id ?? undefined,
      config: {
        ...config,
        ...(cursor ? { cursorCreatedAt: cursor.lastCreatedAt, cursorId: cursor.lastId } : {}),
        size: 20,
      },
    })
    setWidgetData(prev => {
      const existing = prev[widgetId]
      if (!existing) return prev
      return {
        ...prev,
        [widgetId]: {
          ...newData,
          items: [...(existing.items ?? []), ...(newData.items ?? [])],
        },
      }
    })
    if (newData.lastCreatedAt && newData.lastId) {
      widgetCursors.current[widgetId] = { lastCreatedAt: newData.lastCreatedAt, lastId: newData.lastId }
    }
  }, [queryAnalytics, dashboard?.id])

  const handleCreateDashboard = () => {
    createDashboard({
      title: 'New Dashboard',
      description: null,
      isDefault: dashboards.length === 0,
    })
  }

  const handleDeleteCurrentDashboard = () => {
    if (!dashboard?.id) return
    deleteDashboard(dashboard.id)
    setConfirmDelete(false)
    navigate('/dashboard')
  }

  const { can } = usePermission()
  const allowed = dashboard?.allowedActions ?? []
  const canEditDashboard = allowed.includes(PERMISSION_KEYS.DASHBOARD_UPDATE)
  const canDeleteDashboard = allowed.includes(PERMISSION_KEYS.DASHBOARD_DELETE)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
        {dashboards.map((d: Dashboard) => (
          <Button
            key={d.id}
            variant={dashboard?.id === d.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate(`/dashboard/${d.id}`)}
            className="flex-shrink-0"
          >
            <LayoutDashboard className="w-4 h-4 mr-2" />
            {d.title}
          </Button>
        ))}
        {can(PERMISSION_KEYS.DASHBOARD_CREATE) && (
          <Button variant="ghost" size="sm" onClick={handleCreateDashboard} className="flex-shrink-0 text-primary-600 hover:text-primary-700 hover:bg-primary-50">
            <Plus className="w-4 h-4 mr-1" /> New Dashboard
          </Button>
        )}
      </div>

      {dashboard?.isEmpty && dashboard?.isCreator && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex items-start">
          <div>
            <h3 className="font-semibold mb-1">Your dashboard is empty</h3>
            <p className="text-sm">Since there are no widgets visible to you, this dashboard is currently hidden from other users. Add widgets and configure their visibility to share it.</p>
          </div>
        </div>
      )}

      <DashboardBoard
        headerTitle={
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-neutral-900">{dashboard?.title ?? 'Dashboard'}</h1>
            {dashboard?.isDefault && (
              <span className="text-[10px] font-semibold bg-gradient-to-r from-primary-100 to-primary-50 text-primary-700 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Default
              </span>
            )}
            {canDeleteDashboard && dashboards.length > 1 && (
              <button onClick={() => setConfirmDelete(true)} className="ml-1 p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete dashboard">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        }
        headerDescription={dashboard?.description ?? 'Customizable overview of your workspace.'}
        canEdit={canEditDashboard}
        dashboard={dashboard}
        widgets={widgets}
        widgetData={widgetData}
        isLoading={isLoading}
        isSaving={isSaving}
        onCreateWidget={handleCreateWidget}
        onDeleteWidget={handleDeleteWidget}
        onResizeWidget={handleResizeWidget}
        onReorderWidgets={handleReorderWidgets}
        onWidgetsChange={(newWidgets: DashboardWidget[]) => {
          // Find if any widget's dataSource or dataSourceConfig changed and clear stale data
          const changedIds = newWidgets.filter((w, i) => {
            const old = widgets[i]
            if (!old || old.id !== w.id) return false
            return old.dataSource !== w.dataSource || 
              JSON.stringify(old.dataSourceConfig) !== JSON.stringify(w.dataSourceConfig)
          }).map(w => w.id)
          if (changedIds.length > 0) {
            setWidgetData(prev => {
              const next = { ...prev }
              changedIds.forEach(id => delete next[id])
              return next
            })
          }
          setWidgets(newWidgets)
        }}
        onLoadMore={handleLoadMore}
        onSaveSettings={(updates) =>
          persistSettings({
            title: updates.title,
            description: updates.description,
            isDefault: updates.isDefault,
          })
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete "${dashboard?.title ?? 'dashboard'}"?`}
        description="The dashboard and its widgets are removed for everyone. This cannot be undone."
        confirmLabel="Delete dashboard"
        destructive
        onConfirm={handleDeleteCurrentDashboard}
      />
    </div>
  )
}
